'use client';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/use-auth-rbac';
import { useToast } from '@/hooks/use-toast';
import useQueryParams from '@/hooks/useQueryParams';
import authService from '@/services/supabase/auth.service';
import userService from '@/services/supabase/user.service';
import useUserStore from '@/states/user-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, KeyRound, Mail, Rocket, ShieldCheck } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { cn } from '@/lib/utils';

import { ScrollReveal } from '@/components/animations/ScrollReveal';

const loginSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email.' }),
  password: z.string().min(8, { message: 'Password must be at least 8 characters.' }),
});

export const dynamic = 'force-dynamic';

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { toast } = useToast();
  const { isAuthenticated, loading } = useAuth();
  const { setUser, setIsAuthenticated } = useUserStore();
  const { getQueryParams } = useQueryParams();

  const params = getQueryParams() || {};
  const redirect = params.redirect ? decodeURIComponent(params.redirect) : '/dashboard';

  useEffect(() => {
    if (!loading && isAuthenticated) {
      router.replace(redirect);
    }
  }, [isAuthenticated, loading, redirect, router]);

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    setIsLoading(true);
    try {
      const { data, error } = await authService.login(values.email, values.password);

      if (error) {
        toast({
          variant: 'destructive',
          title: 'Authentication Failed',
          description: error.message || 'Invalid credentials. Please try again.',
        });
        return;
      }

      if (data) {
        const userProfile = await userService.getUserById(data.user.id);
        if (!userProfile) throw new Error('Profile not found');

        if (userProfile.status !== 'Active') {
          toast({ variant: 'destructive', title: 'Access Denied', description: 'Your account is suspended.' });
          return;
        }

        setUser(userProfile);
        setIsAuthenticated(true);
        toast({ title: 'Welcome Back', description: 'Access granted. Entering dashboard...' });
      }
    } catch (error) {
      toast({ variant: 'destructive', title: 'System Error', description: 'An unexpected error occurred.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!loading && isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background px-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center space-y-4">
          <div className="w-16 h-16 bg-primary rounded-2xl mx-auto flex items-center justify-center animate-bounce">
            <ShieldCheck className="w-8 h-8 text-primary-foreground" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight">Redirecting...</h2>
          <p className="text-muted-foreground">You are already signed in. Taking you to Capvest dashboard.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative flex items-center justify-center min-h-screen py-12 px-4 font-body bg-slate-50 dark:bg-slate-950">
      {/* Background with Real Estate texture */}
      <div className="absolute inset-0 z-0">
        <Image
          src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop"
          alt="Modern Architecture"
          fill
          className="object-cover opacity-30 dark:opacity-40 grayscale transition-opacity duration-1000"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 to-slate-50 dark:from-slate-950/80 dark:to-slate-950" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <ScrollReveal>
          <Card className="bg-card border-border shadow-xl rounded-2xl overflow-hidden">
            <CardHeader className="space-y-4 pb-8 pt-10 text-center bg-muted/30 border-b border-border">
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                className="w-16 h-16 bg-primary rounded-xl mx-auto flex items-center justify-center shadow-lg transform -rotate-1"
              >
                <Rocket className="w-8 h-8 text-primary-foreground" />
              </motion.div>
              <div className="space-y-2">
                <CardTitle className="text-3xl font-bold tracking-tight">Welcome Back</CardTitle>
                <CardDescription className="text-muted-foreground font-medium">Access your investment portfolio</CardDescription>
              </div>
            </CardHeader>

            <CardContent className="pt-10 px-8 pb-12">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <FormLabel className="text-muted-foreground font-semibold text-xs uppercase tracking-wider ml-1">Email Address</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <Input
                              placeholder="m@example.com"
                              {...field}
                              className="h-12 pl-12 rounded-xl border-border bg-background transition-all"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <div className="flex items-center justify-between ml-1">
                          <FormLabel className="text-muted-foreground font-semibold text-xs uppercase tracking-wider">Password</FormLabel>
                          <Link href="#" className="text-xs font-bold text-primary hover:underline">Forgot?</Link>
                        </div>
                        <FormControl>
                          <div className="relative">
                            <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <Input
                              type="password"
                              placeholder="••••••••"
                              {...field}
                              className="h-12 pl-12 rounded-xl border-border bg-background transition-all"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="pt-2">
                    <Button
                      type="submit"
                      className="w-full h-12 bg-primary hover:bg-primary/95 text-primary-foreground rounded-xl font-bold text-base shadow-md transition-all active:scale-[0.98]"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                          Authenticating...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          Sign In <ChevronRight className="w-4 h-4" />
                        </div>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>

              <div className="mt-10 text-center">
                <p className="text-muted-foreground text-sm">
                  New to Cardone Capvest?{' '}
                  <Link
                    href={params.redirect ? `/signup?redirect=${encodeURIComponent(params.redirect)}` : "/signup"}
                    className="text-primary font-bold hover:underline underline-offset-4"
                  >
                    Register here
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </ScrollReveal>
      </motion.div>
    </div>
  );
}
